import {
    cents,
    dateDay,
    defineFilters,
    enumOne,
    multiEnum,
    objectId,
    text,
    type FilterOption
} from '@/components/kit/filters'
import { TRANSACTION_META, TransactionKind, type TransactionSortField, type TransactionType } from '@/types/transaction'

/** Ordered credit first, then debit, then neutral: the way an operator reads a ledger. */
export const TRANSACTION_TYPE_OPTIONS: FilterOption<TransactionType>[] = Object.entries(TRANSACTION_META)
    .sort(([, a], [, b]) => {
        const order = { CREDIT: 0, DEBIT: 1, NEUTRAL: 2 } as const
        return order[a.direction] - order[b.direction] || a.label.localeCompare(b.label)
    })
    .map(([value, meta]) => ({ value: value as TransactionType, label: meta.label }))

export const TRANSACTION_KIND_OPTIONS: FilterOption<TransactionKind>[] = [
    { value: TransactionKind.BALANCE, label: 'Balance', hint: 'Moves money in the wallet' },
    { value: TransactionKind.RECEIVE_ITEMS, label: 'Items in', hint: 'Cards added to the inventory' },
    { value: TransactionKind.REMOVE_ITEMS, label: 'Items out', hint: 'Cards taken out of the inventory' }
]

const SORT_OPTIONS: FilterOption<TransactionSortField>[] = [
    { value: 'createdAt', label: 'Date' },
    { value: 'amount', label: 'Amount' },
    { value: 'type', label: 'Type' }
]

const ORDER_OPTIONS: FilterOption<'asc' | 'desc'>[] = [
    { value: 'desc', label: 'Descending' },
    { value: 'asc', label: 'Ascending' }
]

export const transactionFilters = defineFilters(
    {
        search: text({ label: 'Search' }),
        user: objectId({ label: 'User', apiKey: 'userId' }),
        types: multiEnum(TRANSACTION_TYPE_OPTIONS, { label: 'Type' }),
        kinds: multiEnum(TRANSACTION_KIND_OPTIONS, { label: 'Kind' }),
        dateFrom: dateDay({ label: 'From', boundary: 'start' }),
        dateTo: dateDay({ label: 'To', boundary: 'end' }),
        amountMin: cents({ label: 'Min amount' }),
        amountMax: cents({ label: 'Max amount' }),
        sortField: enumOne(SORT_OPTIONS, 'createdAt', { label: 'Sort' }),
        sortDir: enumOne(ORDER_OPTIONS, 'desc', { label: 'Order' })
    },
    { defaultLimit: 25 }
)

export type TransactionSearch = ReturnType<typeof transactionFilters.parse>
